/**
 * clay.ts — The modelling clay everything in the game is made from.
 *
 * The whole town is meant to look like it was rolled and pressed out of Play-doh
 * on a kitchen table. That look is three things, and this module owns all three
 * so nothing can be built out of the wrong stuff:
 *
 * 1. **No sharp edges.** Real clay can't hold a crisp 90° corner — it slumps.
 *    Every box here is a `RoundedBoxGeometry` whose corner radius scales with the
 *    object's *smallest* dimension, so a chunky torso rounds off generously while
 *    a thin windowpane only softens slightly. This is the single biggest tell;
 *    a hard-edged box dropped into the scene reads as plastic immediately.
 * 2. **A matte, waxy surface.** `MeshStandardMaterial` at high roughness and zero
 *    metalness. Plus a trick: the material is given a faint `emissive` in its own
 *    base colour (`CLAY.SUBSURFACE`), so faces turned away from the sun glow dimly
 *    instead of falling to flat shade. That's a cheap stand-in for the way light
 *    creeps *through* the surface of real clay, and it's what stops the palette
 *    going muddy in shadow.
 * 3. **Nothing perfectly straight.** `clayWobble()` gives each static prop a
 *    fraction of a degree of tilt, so the town looks hand-placed rather than
 *    stamped from a grid.
 *
 * ## Caching
 *
 * Geometries and materials are cached and **shared** across every mesh that asks
 * for the same dimensions or colour. Rounded boxes carry far more vertices than
 * plain ones, and the town builds hundreds of them, so this matters. The
 * consequence: **never call `.dispose()` on anything this module hands you**, and
 * never mutate a returned material — you'd be editing every other object using
 * that colour. Pass `opts` to get an uncached one-off instead.
 *
 * Tuning lives in `config/constants.ts` → `CLAY`.
 */
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { CLAY, OUT_OF_BOUNDS } from '../config/constants';
import { activeLevel } from '../config/level';
import { hash2 } from '../utils/math';

const geoCache = new Map<string, THREE.BufferGeometry>();
const matCache = new Map<number, THREE.MeshStandardMaterial>();

/**
 * The corner radius for a block of these dimensions.
 *
 * Scaled off the smallest side so proportionally-thin things don't round away to
 * nothing, and hard-capped just under half that side — `RoundedBoxGeometry`
 * produces inside-out geometry if the radius reaches half the extent.
 */
export function clayRadius(w: number, h: number, d: number): number {
  const min = Math.min(w, h, d);
  return Math.min(min * CLAY.ROUND, min * 0.49);
}

/**
 * How finely to subdivide a block of this size.
 *
 * A rounded box costs ~16× the triangles of a plain one, and the town builds
 * hundreds of them — the window sills alone are instanced into the high
 * hundreds. Detail is spent where it can actually be seen: anything whose
 * smallest side is under `CLAY.DETAIL_MIN` (sills, glazing bars, door
 * furniture) is a few pixels across at gameplay distance, so it drops to a
 * single-segment chamfer that still catches light but costs a fraction as much.
 */
function segmentsFor(w: number, h: number, d: number): number {
  return Math.min(w, h, d) < CLAY.DETAIL_MIN ? 1 : CLAY.SEGMENTS;
}

/** Shared rounded-box geometry for these dimensions. Do not dispose. */
export function clayGeometry(w: number, h: number, d: number): THREE.BufferGeometry {
  // Round the key so near-identical sizes share one geometry rather than each
  // spawning its own; the visual difference at 0.01 units is nil.
  const key = `${w.toFixed(2)}x${h.toFixed(2)}x${d.toFixed(2)}`;
  let geo = geoCache.get(key);
  if (!geo) {
    geo = new RoundedBoxGeometry(w, h, d, segmentsFor(w, h, d), clayRadius(w, h, d));
    geoCache.set(key, geo);
  }
  return geo;
}

/**
 * Shared matte-clay material in `color`. Do not dispose or mutate.
 *
 * Pass `opts` for a one-off (emissive props, transparency) — those are *not*
 * cached, since the caller is by definition asking for something different.
 */
export function clayMaterial(
  color: number,
  opts?: Partial<THREE.MeshStandardMaterialParameters>,
): THREE.MeshStandardMaterial {
  if (opts) return buildClayMaterial(color, opts);
  let m = matCache.get(color);
  if (!m) {
    m = buildClayMaterial(color);
    matCache.set(color, m);
  }
  return m;
}

/**
 * The playable rectangle as `(x, y, w, h)` on the ground plane, shared by every
 * material that carries the filter below. One object, mutated in place, so a
 * level change reaches materials cached from the last one.
 */
export const boundsUniform = { value: new THREE.Vector4() };

/** Re-read the playable rectangle from the level being played. */
export function updateBoundsUniform(): void {
  const { x, y, w, h } = activeLevel().bounds;
  boundsUniform.value.set(x, y, w, h);
}

/**
 * Hang the out-of-bounds filter on a material. Call it on anything the town is
 * built from; see `greyOutsideBounds` for what it does and why it lives here.
 */
export function applyBoundsGreyscale(mat: THREE.Material): void {
  // One shared function rather than a closure per material: three's default
  // `customProgramCacheKey()` is `onBeforeCompile.toString()`, so identical
  // source means the whole town still shares compiled programs.
  mat.onBeforeCompile = greyOutsideBounds;
}

/** A GLSL float literal — `1` is an int in GLSL and won't compile as one. */
const glsl = (n: number): string => n.toFixed(4);

/**
 * Wash the colour out of everything standing outside `LevelSpec.bounds`.
 *
 * The surround is scenery: the walker is clamped to `bounds` and the town
 * carries on past it, so the ring needs to read as somewhere you can't go
 * rather than as somewhere you haven't been yet.
 *
 * It used to be **painted** — a grey tarmac, a grey paving, grey walls, grey
 * leaves, each picked by testing an element's centre against `bounds`. Three
 * bugs came out of that and they were all the same bug: *an element is not a
 * place*. A cottage straddling the boundary got one answer for the whole
 * building, so the row fronting the walk from outside stayed in full colour; a
 * footway span whose centre was inside kept its colour along the half that was
 * outside; and anything whose colour came from somewhere else — every instanced
 * facade, every pane in the distant ring — was never painted at all.
 *
 * So the test moved to the one place the answer actually differs: the fragment.
 * The boundary now falls exactly on `bounds` whatever is standing across it, and
 * the town is built from the town's own palette everywhere. Three details hold
 * it together:
 *
 * - **Instances and batches carry their own transform.** `modelMatrix` belongs
 *   to the collector, not the instance, so folding in `instanceMatrix` under the
 *   same defines `project_vertex` uses is not a nicety — without it every
 *   window, sill, ridge tile and bush in the game tests as standing at the
 *   batch's origin, which is to say the meshes there are most of are the ones
 *   the filter misses.
 * - **It runs last**, after tone mapping and fog, so it is a filter over the
 *   finished picture rather than a change to the paint. That's what keeps the
 *   haze over the surround the same haze as over the town.
 * - **It desaturates and barely dims** (`OUT_OF_BOUNDS`). Darkening the ring
 *   was the first attempt and it read as night falling over next door; taking
 *   the colour out says "not here" without saying anything about the weather.
 */
function greyOutsideBounds(shader: THREE.WebGLProgramParametersWithUniforms): void {
  shader.uniforms.uLevelBounds = boundsUniform;

  shader.vertexShader = shader.vertexShader
    .replace(
      '#include <common>',
      `#include <common>
      varying vec3 vBoundsPos;`,
    )
    // After `project_vertex`, so `transformed` has been through the morph and
    // skin chunks and the instancing defines are the ones in force.
    .replace(
      '#include <project_vertex>',
      `#include <project_vertex>
      vec4 boundsPos = vec4( transformed, 1.0 );
      #ifdef USE_BATCHING
        boundsPos = batchingMatrix * boundsPos;
      #endif
      #ifdef USE_INSTANCING
        boundsPos = instanceMatrix * boundsPos;
      #endif
      vBoundsPos = ( modelMatrix * boundsPos ).xyz;`,
    );

  shader.fragmentShader = shader.fragmentShader
    .replace(
      '#include <common>',
      `#include <common>
      uniform vec4 uLevelBounds;
      varying vec3 vBoundsPos;`,
    )
    .replace(
      '#include <dithering_fragment>',
      `#include <dithering_fragment>
      {
        // Gameplay y is world z — see THE COORDINATE RULE in AGENTS.md.
        vec2 under = uLevelBounds.xy - vBoundsPos.xz;
        vec2 over = vBoundsPos.xz - ( uLevelBounds.xy + uLevelBounds.zw );
        float past = max( max( under.x, over.x ), max( under.y, over.y ) );
        // A few units of feather. The edge is axis-aligned on screen along the
        // ground, where a hard step is clean, but it crosses roofs and canopies
        // at every other angle, and there it crawls.
        float away = smoothstep( 0.0, ${glsl(OUT_OF_BOUNDS.FADE)}, past );
        float grey = dot( gl_FragColor.rgb, vec3( 0.2126, 0.7152, 0.0722 ) );
        gl_FragColor.rgb = mix(
          gl_FragColor.rgb,
          vec3( grey ),
          away * ${glsl(OUT_OF_BOUNDS.DESATURATION)}
        );
        gl_FragColor.rgb *= 1.0 - away * ${glsl(OUT_OF_BOUNDS.DIM)};
      }`,
    );
}

function buildClayMaterial(
  color: number,
  opts: Partial<THREE.MeshStandardMaterialParameters> = {},
): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    color,
    roughness: CLAY.ROUGHNESS,
    metalness: 0,
    // Faint self-glow in the object's own hue: shadowed clay stays coloured
    // instead of going flat and grey. See the note at the top of the file.
    emissive: color,
    emissiveIntensity: CLAY.SUBSURFACE,
    // Smooth clay gradients band badly in 8-bit without this.
    dithering: true,
    ...opts,
  });
  applyBoundsGreyscale(mat);
  return mat;
}

/** A rounded clay block centred at (x, y, z), casting and receiving shadow. */
export function clayBox(
  w: number,
  h: number,
  d: number,
  color: number,
  x = 0,
  y = 0,
  z = 0,
): THREE.Mesh {
  const mesh = new THREE.Mesh(clayGeometry(w, h, d), clayMaterial(color));
  mesh.position.set(x, y, z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * A flat horizontal plane at height `y`, sized w×d, centred at (cx, cz).
 *
 * Ground surfaces — paving, tarmac, park grass, patios — stay flat planes rather
 * than rounded clay blocks. They're seen edge-on from this camera, so rounding
 * buys nothing and costs vertices over the whole map. They still take the clay
 * *material*, so paving reads as rolled-flat clay rather than a different
 * substance from the buildings.
 *
 * `own` forces an uncached material for callers that need to mutate it (the
 * home pad fades itself). Mutating a shared clay material would edit every
 * other surface using that colour.
 */
export function claySlab(
  w: number,
  d: number,
  color: number,
  cx: number,
  y: number,
  cz: number,
  own = false,
): THREE.Mesh {
  return layFlat(new THREE.PlaneGeometry(w, d), own ? clayMaterial(color, {}) : clayMaterial(color), cx, y, cz);
}

/**
 * The same flat plane, but carrying a material of the caller's own — a patterned
 * one, usually (see `render/textures.ts`).
 *
 * Split out because the ground is where a pattern pays best: roads, footways and
 * parks are between them the largest surface in the game, and a flat fill of
 * colour down there sits very badly next to a coursed brick wall.
 */
export function texturedSlab(
  geo: THREE.BufferGeometry,
  material: THREE.Material,
  cx: number,
  y: number,
  cz: number,
): THREE.Mesh {
  return layFlat(geo, material, cx, y, cz);
}

function layFlat(
  geo: THREE.BufferGeometry,
  material: THREE.Material,
  cx: number,
  y: number,
  cz: number,
): THREE.Mesh {
  const mesh = new THREE.Mesh(geo, material);
  mesh.rotation.x = -Math.PI / 2; // lay flat
  mesh.position.set(cx, y, cz);
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Shared ball geometry. Do not dispose.
 *
 * `detail` scales the segment count for balls that are numerous and small —
 * flowers in a window box are two units across, where the full sphere is as many
 * triangles as the roof it sits under.
 */
export function claySphereGeometry(r: number, detail = 1): THREE.BufferGeometry {
  const seg = Math.max(4, Math.round(CLAY.SPHERE_SEGMENTS * detail));
  const key = `sphere${r.toFixed(2)}@${seg}`;
  let geo = geoCache.get(key);
  if (!geo) {
    geo = new THREE.SphereGeometry(r, seg, Math.max(2, Math.round(seg / 2)));
    geoCache.set(key, geo);
  }
  return geo;
}

/** A clay ball — heads, noses, knobs. */
export function claySphere(r: number, color: number, x = 0, y = 0, z = 0): THREE.Mesh {
  const mesh = new THREE.Mesh(claySphereGeometry(r), clayMaterial(color));
  mesh.position.set(x, y, z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * A rolled clay sausage — limbs, tails, chimney pots. Capsules read far more
 * hand-rolled than even a heavily-rounded box, and they're what stops the
 * characters looking like stacks of dice.
 *
 * `len` is the length of the straight section; the caps add `r` at each end.
 */
export function clayPill(r: number, len: number, color: number): THREE.Mesh {
  const key = `pill${r.toFixed(2)}x${len.toFixed(2)}`;
  let geo = geoCache.get(key);
  if (!geo) {
    geo = new THREE.CapsuleGeometry(r, len, CLAY.SEGMENTS, CLAY.SPHERE_SEGMENTS);
    geoCache.set(key, geo);
  }
  const mesh = new THREE.Mesh(geo, clayMaterial(color));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * A flat clay shape pressed out with a cutter — arched doors, gable ends.
 *
 * The fourth member of the family, for the shapes a box, a ball and a sausage
 * can't make: anything with a curved or angled *outline*. The bevel is what
 * keeps it clay — an extrusion with square edges is the one hard-edged thing
 * that would stand out here, and the bevel rounds the outline exactly the way
 * `clayGeometry` rounds a box.
 *
 * The shape is drawn in x (across) and y (up) and extruded toward +z, so the
 * caller places it with a yaw and it faces out of a wall. Geometry is **not**
 * cached — every caller passes a different outline — so these are for the
 * couple of dozen one-offs in the town, never for anything repeated.
 */
export function clayExtrude(
  shape: THREE.Shape,
  depth: number,
  color: number,
  bevel: number = CLAY.EXTRUDE_BEVEL,
): THREE.Mesh {
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(depth - bevel * 2, 0.01),
    curveSegments: CLAY.SPHERE_SEGMENTS,
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 2,
  });
  // Extrusion starts at z = 0 and the bevel pushes back past it; shift so the
  // solid runs from 0 to `depth` and the caller can treat it as a slab standing
  // that far off the wall.
  geo.translate(0, 0, bevel);
  const mesh = new THREE.Mesh(geo, clayMaterial(color));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Nudge a static prop off true by a fraction of a degree, so the town reads as
 * hand-placed rather than stamped out on a grid.
 *
 * Deterministic in the object's own position: the same building wobbles the same
 * way on every run, which keeps restarts and screenshot comparisons stable.
 * Rotation only — moving things would desync them from their collision rect.
 */
export function clayWobble(object: THREE.Object3D, seedX: number, seedZ: number): void {
  object.rotation.x += (hash2(seedX, seedZ) - 0.5) * 2 * CLAY.WOBBLE;
  object.rotation.y += (hash2(seedZ, seedX + 17) - 0.5) * 2 * CLAY.WOBBLE_YAW;
  object.rotation.z += (hash2(seedX + 31, seedZ) - 0.5) * 2 * CLAY.WOBBLE;
}
